import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Button, SingleLineText } from '@bloomays-lib/ui.shared';
import { BrowserUploadFile } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { Upload } from './Upload';

export type ActionUploadInvoiceProps = {
  /** Absent while the Bloomer hasn't declared his CRA: it is created on upload (TEC-3262). */
  activityId?: string | null;
  missionLabel?: string;
  activityMonth?: Date;
  /** Label of the uploaded file, e.g. "la facture Bloomer" / "la facture additionnelle". */
  fileLabel?: string;
  onUpload: (file: BrowserUploadFile) => Promise<void>;
  onClose: () => void;
};

/**
 * Upload modal for a Bloomer invoice file (main or additional) of a CRA — same modal
 * pattern as ActionForceCRA / ActionInvoice.
 */
const ActionUploadInvoice = ({
  activityId,
  missionLabel,
  activityMonth,
  fileLabel = 'la facture Bloomer',
  onUpload,
  onClose,
}: ActionUploadInvoiceProps) => {
  const [files, setFiles] = useState<BrowserUploadFile[]>([]);
  const [loading, setLoading] = useState(false);

  return (
    <Container>
      <Title>{missionLabel || 'Facture Bloomer'}</Title>
      <SingleLineText variant="caption" text={`Activité : ${activityId || 'CRA à créer'}`} />
      {activityMonth ? (
        <SingleLineText variant="caption" text={`Mois : ${convertDateToStringFormat(activityMonth, 'MM/yyyy')}`} />
      ) : null}

      <SingleLineText text={`Uploader ${fileLabel} (PDF)`} />
      <Upload
        id="invoiceS3"
        name="invoiceS3"
        placeholder="Facture"
        textLabel="Choisir le fichier"
        fileTypes={['.pdf']}
        maxFiles={1}
        maxLength={5000000}
        displayIcons={true}
        autoload={true}
        preloadedFiles={files}
        handleChange={(accepted: BrowserUploadFile[]) => setFiles(accepted)}
      />

      <Button
        textButton="Uploader !"
        disable={files.length === 0 || loading}
        onClick={async () => {
          if (!files[0]) return;
          setLoading(true);
          try {
            await onUpload(files[0]);
            onClose();
          } finally {
            setLoading(false);
          }
        }}
      />
    </Container>
  );
};

export default ActionUploadInvoice;
export { ActionUploadInvoice };

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  alignItems: 'center',
  textAlign: 'center',
  padding: 8,
}));

const Title = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));
